import "./types.js";
import Cell from "./Cell.js";
import {
  compareNumber,
  isSequential,
  last,
  MOVE_SRC_DOWN,
  MOVE_SRC_LEFT,
  MOVE_SRC_RIGHT,
  MOVE_SRC_UP,
  range,
  unique,
  validate
} from "./util.js";

/**
 * module description
 * @module Types
 */
/**
 * ContextActions
 * All the values requried for rendering and performing context menu
 * actions depending on whether the actions are for a cell, row or column.
 *
 * @typedef {Object} ContextActions
 * @property {object} mergeCells
 *           An object with { columnIndexes, rowIndexes } properties. The cells to
 *           merge based on their rown and column indexes as you can only merge one
 *           block of cells at a time.
 * @property {Array} unmergeCells
 *           List of target cells to unmerge, can unmerge many "blocks" of merged
 *           cells at once.
 * @property {Array} addRows List of row indexes to add.
 * @property {Array} deleteRows List of row indexes to delete.
 * @property {Array} addColumns List of column indexes to add.
 * @property {Array} deleteColumns List of column indexes to delete.
 * @property {object} moveColumnsLeft An object with { srcIndexes, destIndex } properties.
 * @property {object} moveColumnsRight An object with { srcIndexes, destIndex } properties.
 * @property {object} moveRowsUp An object with { srcIndexes, destIndex } properties.
 * @property {object} moveRowsDown An object with { srcIndexes, destIndex } properties.
 */

/**
 * module description
 * @module Table2
 */

/**
 * Organise and manipulate a HTML table.
 */
class Table2EditorOps {
  // #layout
  // #numberOfCols
  // #numberOfRows

  /**
   * Setup table with layout.
   *
   * @param {object} layout An array of cells within an array of rows.
   */
  constructor(layout) {
    validate.constructor(layout);
    this._layout = layout.map(row => row.map(cell => new Cell(cell)));
    this._numberOfCols = layout[0].length - 1;
    this._numberOfRows = layout.length - 1;
  }

  /**
   * Get the raw table layout stripped of any cell instances.
   *
   * The layout of a table is represented by an array of rows,
   * with each row an array of cells, and each cell object containing
   * id and coordinates properties. coordinates are an array of row & column
   * indexes representing the target cell. The target cell will be its
   * own position if it is not merged or if other cells are merged into it.
   * If the cell is merged into another cell, the coordinates will be for the
   * other "target" cell.
   * This will strip out the Cell class from the private layout class property.
   *
   * @returns {Array} Table layout, an array of cell positions within an array of rows.
   */
  getLayout() {
    return this._layout.map(row => row.map(cell => cell.get()));
  }

  /**
   * Get the Cell instance in the private layout field by its id.
   *
   * @param {string} id UUID of the cell to get.
   * @returns {Cell} cell instance
   */
  _getCellById(id) {
    return this._layout.flatMap(row => row.filter(cell => cell.id === id))[0];
  }

  // 0 index
  getNumberOfCols() {
    return (this._numberOfCols = this._layout[0].length - 1);
  }

  // 0 index
  getNumberOfRows() {
    return (this._numberOfRows = this._layout.length - 1);
  }

  /**
   * Get the 0-based order of cell within the layout.
   * The top-left cell is first, the bottom-right cell is last.
   *
   * @param {string} id UUID of the cell to check for index.
   * @returns {number} 0-based index of the cell within the flattened layout nested array.
   */
  _getCellIndexById(cellId) {
    return this._layout.flat().findIndex(cell => cell.id === cellId);
  }

  /**
   *
   * @param {number} currentRowIndex row index of cell
   * @param {number} currentcolumnIndex column index of cell
   * @returns {number} number of rows for cell to span
   */
  computeRowSpan(currentRowIndex, currentcolumnIndex) {
    const lastRowIndex = this._layout.findLastIndex((row, rowIndex) => {
      if (rowIndex > currentRowIndex) {
        const currentCell = this._layout[currentRowIndex][currentcolumnIndex];
        return Boolean(row.findLast(cell => cell.isSame(currentCell)));
      }
      return false;
    });
    return lastRowIndex === -1 ? 1 : lastRowIndex + 1 - currentRowIndex;
  }

  /**
   *
   * @param {number} currentRowIndex row index of cell
   * @param {number} currentcolumnIndex column index of cell
   * @returns {number} number of columns for cell to span
   */
  computeColSpan(currentRowIndex, currentcolumnIndex) {
    const currentRow = this._layout[currentRowIndex];
    const currentCell = currentRow[currentcolumnIndex];
    const nextcolumnIndex = currentRow.findLastIndex(
      (nextCell, columnIndex) => {
        if (columnIndex > currentcolumnIndex && currentCell.isSame(nextCell)) {
          return true;
        }
        return false;
      }
    );
    return nextcolumnIndex === -1
      ? 1
      : nextcolumnIndex + 1 - currentcolumnIndex;
  }

  _isColumnOutOfBounds(columnIndex) {
    return columnIndex < 0 || columnIndex > this._numberOfCols;
  }

  _isRowOutOfBounds(rowIndex) {
    return rowIndex < 0 || rowIndex > this._numberOfRows;
  }

  /**
   * Get sub-selection of table layout.
   * Get a block of Cell.
   *
   * @private
   * @param {Array} rowIndexes index of rows to select.
   * @param {Array} columnIndexes index of columns to select.
   * @returns {Array} Array of cells within an array of rows.
   */
  _getSelection(rowIndexes, columnIndexes) {
    return this._layout.flatMap((row, rowIndex) =>
      !rowIndexes.includes(rowIndex)
        ? []
        : [
            row.flatMap((cell, columnIndex) =>
              !columnIndexes.includes(columnIndex) ? [] : [cell]
            )
          ]
    );
  }

  /**
   * After adding/moving/deleting/duplicating table columns, will
   * need to reset the column order by "bumping" any relevant existing
   * columns to account for the new amount of columns
   *
   * @private
   * @param {number} offset Offset to apply to "bumped" columns.
   * @param {Array} ignoreIndexes Columns that should not be "bumped".
   * @param {Array} srcIndexes Indexes of newly added columns.
   * @param {number} srcOffset Offset to apply to newly added columns.
   */
  _resetColumnOrder(offset, ignoreIndexes, srcIndexes = [], srcOffset) {
    this._numberOfCols = this._layout[0].length - 1;
    this._layout = this._layout.map((row, rowIndex) =>
      row.map((cell, columnIndex) => {
        if (srcIndexes.includes(columnIndex)) {
          return cell.update([cell.row, cell.column + srcOffset]);
        }
        if (ignoreIndexes.includes(columnIndex)) {
          return cell;
        }
        return ignoreIndexes.includes(cell.column)
          ? cell
          : cell.update([cell.row, cell.column + offset]);
      })
    );
  }

  /**
   * After adding/moving/deleting/duplicating table rows, will
   * need to reset the row order by "bumping" any relevant existing
   * rows to account for the new amount of rows.
   *
   * @private
   * @param {number} offset Offset to apply to "bumped" rows.
   * @param {Array} ignoreIndexes Rows that should not be "bumped".
   * @param {Array} srcIndexes Indexes of newly added rows.
   * @param {number} srcOffset Offset to apply to newly added rows.
   */
  _resetRowOrder(offset, ignoreIndexes, srcIndexes = [], srcOffset) {
    this._numberOfRows = this._layout.length - 1;
    this._layout = this._layout.map((row, rowIndex) => {
      if (srcIndexes.includes(rowIndex)) {
        return row.map(cell =>
          cell.update([cell.row + srcOffset, cell.column])
        );
      }
      if (ignoreIndexes.includes(rowIndex)) {
        return row;
      }
      return row.map(cell =>
        ignoreIndexes.includes(cell.row)
          ? cell
          : cell.update([cell.row + offset, cell.column])
      );
    });
  }

  /**
   * Select rowspan
   * Extend the selected rowIndexes to include any direct compound row.
   * Overlapping compound rows can only be picked up if they are direct compound row,
   * so keep calling this method until the result is the same as the previous call.
   *
   * @private
   * @param {Array} rowIndexes List of original rowIndexes
   * @param {Array} columnIndexesToLimit Optional list of columnIndexes to limit to.
   * @returns {Array} List of rowIndexes that includes directly overlapping compound rows.
   */
  _selectRowspan(rowIndexes, columnIndexesToLimit) {
    const limitToCols =
      columnIndexesToLimit || range(0, this.getNumberOfCols());
    // get references before rowIndexes
    const referencesBeforeSelectedRows = this._layout.flatMap((row, rowIndex) =>
      !rowIndexes.includes(rowIndex)
        ? []
        : row.flatMap((cell, columnIndex) =>
            cell.row !== rowIndex && limitToCols.includes(columnIndex)
              ? cell.row
              : []
          )
    );

    // get targets after rowIndexes
    const targetsAfterSelectedRows = this._layout.flatMap((row, rowIndex) =>
      rowIndex <= last(rowIndexes)
        ? []
        : row.flatMap((cell, columnIndex) =>
            (rowIndexes.includes(cell.row) || cell.row < rowIndexes[0]) &&
            limitToCols.includes(columnIndex)
              ? rowIndex
              : []
          )
    );

    const allRowIndexes = unique([
      ...rowIndexes,
      ...referencesBeforeSelectedRows,
      ...targetsAfterSelectedRows
    ]).sort(compareNumber);

    return range(allRowIndexes[0], last(allRowIndexes));
  }

  /**
   * Select colspan
   * Extend the selected columnIndexes to include any direct compound column.
   * Overlapping compound column can only be picked up if they are direct compound column,
   * so keep calling this method until the result is the same as the previous invocation.
   *
   * @private
   * @param {Array} columnIndexes List of original columnIndexes
   * @param {Array} rowIndexesToLimit Optional list of rowIndexes to limit to.
   * @returns {Array} List of columnIndexes that includes directly overlapping compound columns.
   */
  _selectColspan(columnIndexes, rowIndexesToLimit) {
    const limitToRows = rowIndexesToLimit || range(0, this.getNumberOfRows());
    // get references before columnIndexes
    const referencesBeforeSelectedColumns = this._layout.flatMap(
      (row, rowIndex) =>
        row.flatMap((cell, columnIndex) =>
          limitToRows.includes(rowIndex) &&
          columnIndexes.includes(columnIndex) &&
          cell.column !== columnIndex
            ? cell.column
            : []
        )
    );

    // get targets after columnIndexes
    const targetsAfterSelectedColumns = this._layout.flatMap((row, rowIndex) =>
      row.flatMap((cell, columnIndex) =>
        limitToRows.includes(rowIndex) &&
        columnIndex > last(columnIndexes) &&
        (columnIndexes.includes(cell.column) || cell.column < columnIndexes[0])
          ? columnIndex
          : []
      )
    );

    const allcolumnIndexes = unique([
      ...columnIndexes,
      ...referencesBeforeSelectedColumns,
      ...targetsAfterSelectedColumns
    ]).sort(compareNumber);

    return range(allcolumnIndexes[0], last(allcolumnIndexes));
  }

  /**
   * Select rows
   * Will extend the selected row(s) if it is part of a compound row(s).
   *
   * @param {Array} rowIndexes Array of numbers representing a list of sequential row indexes.
   * @returns {Array} List of row indexes.
   */
  selectRows(rowIndexes) {
    validate.selectRows(rowIndexes);
    // early return
    if (rowIndexes.some(rowIndex => this._isRowOutOfBounds(rowIndex))) {
      return false;
    }

    // loop over each rowspan to keep extending to overlapping rowspans
    let rows = rowIndexes;
    while (this._selectRowspan(rows).toString() !== rows.toString()) {
      rows = this._selectRowspan(rows);
    }
    return rows;
  }

  /**
   * Select columns
   * Will extend the selected column(s) if it is part of a compound column(s).
   *
   * @param {number} columnIndexes Array of numbers representing a list of sequential column indexes.
   * @returns {Array} List of column indexes.
   */
  selectColumns(columnIndexes) {
    validate.selectColumns(columnIndexes);
    // early return
    if (columnIndexes.some(rowIndex => this._isColumnOutOfBounds(rowIndex))) {
      return false;
    }

    // loop over each colspan to keep extending to overlapping colspans
    let columns = columnIndexes;
    while (this._selectColspan(columns).toString() !== columns.toString()) {
      columns = this._selectColspan(columns);
    }
    return columns;
  }

  /**
   * Extends supplied range of cells to include any cells merged with
   * supplied cells.
   *
   * @param {Array} cellIds List of cell UUIDs.
   * @returns {Array} Array of cells.
   */
  selectCells(cellIds) {
    const suppliedCells = cellIds.map(cellId => this._getCellById(cellId));
    return this._layout
      .flatMap(row =>
        row.flatMap(cell =>
          suppliedCells.flatMap(suppliedCell =>
            cell.isSame(suppliedCell) ? cell : []
          )
        )
      )
      .flat();
  }

  /**
   * Get actions for row(s)
   * NOTE: only getActionsForCells allow merging merged cells together
   *
   * @param {number} rowIndexes List of selected sequential row indexes.
   * @returns {ContextActions} Values for rendering and performing context menu actions.
   */
  getActionsForRows(rowIndexes) {
    validate.getActionsForRows(rowIndexes);
    // early return
    if (rowIndexes.some(rowIndex => this._isRowOutOfBounds(rowIndex))) {
      return false;
    }

    const allRowIndexes = this.selectRows(rowIndexes);

    const areAllRowsSelected =
      allRowIndexes.toString() === range(0, this._numberOfRows).toString();
    const columnIndexes = range(0, this._numberOfCols);
    const allSelectedCells = this._getSelection(
      allRowIndexes,
      columnIndexes
    ).flat();
    const areAllMergedCells = !allSelectedCells.some(
      cell =>
        allSelectedCells.filter(selectedCell => cell.isSame(selectedCell))
          .length < 2
    );
    const selectedCellsAreForAllRows =
      rowIndexes.length - 1 === this._numberOfRows;
    return {
      // If already merged or only 1 row and 1 column is selected, do not show mergeCells
      ...(areAllMergedCells ||
      (allRowIndexes.length < 2 && columnIndexes.length < 2)
        ? {}
        : { mergeCells: { rowIndexes: allRowIndexes, columnIndexes } }),
      // If all cells of selected rows are merged cells, show unmergeCells
      ...(!areAllMergedCells
        ? {}
        : {
            unmergeCells: Cell.getUnique(allSelectedCells)
              .map(cell => this._layout[cell.row][cell.column])
              .map(cell => cell.get())
          }),
      // If all rows are selected do not show deleteRows and addRows
      ...(selectedCellsAreForAllRows ? {} : { deleteRows: rowIndexes }),
      addRow: [last(rowIndexes)],
      duplicateRows: allRowIndexes,
      // If last row is selected do not show moveRowsDown
      ...(areAllRowsSelected || last(allRowIndexes) === this._numberOfRows
        ? {}
        : {
            moveRowsDown: {
              srcIndexes: allRowIndexes,
              destIndex: last(this.selectRows([last(allRowIndexes) + 1]))
            }
          }),
      // If first row is selected do not show moveRowsUp
      ...(areAllRowsSelected || allRowIndexes[0] === 0
        ? {}
        : {
            moveRowsUp: {
              srcIndexes: allRowIndexes,
              destIndex: this.selectRows([allRowIndexes[0] - 1])[0]
            }
          })
    };
  }

  /**
   * Get actions for column(s)
   * NOTE: only getActionsForCells allow merging merged cells together
   *
   * @param {number} columnIndexes List of selected sequential row indexes.
   * @returns {ContextActions} Values for rendering and performing context menu actions.
   */
  getActionsForColumns(columnIndexes) {
    validate.getActionsForColumns(columnIndexes);
    // early return
    if (
      columnIndexes.some(columnIndex => this._isColumnOutOfBounds(columnIndex))
    ) {
      return false;
    }

    const allColumnIndexes = this.selectColumns(columnIndexes);

    const areAllColumnsSelected =
      allColumnIndexes.toString() === range(0, this._numberOfCols).toString();
    const rowIndexes = range(0, this._numberOfRows);
    const allSelectedCells = this._getSelection(
      rowIndexes,
      allColumnIndexes
    ).flat();
    const areAllMergedCells = !allSelectedCells.some(
      cell =>
        allSelectedCells.filter(selectedCell => cell.isSame(selectedCell))
          .length < 2
    );
    const selectedCellsAreForAllColumns =
      columnIndexes.length - 1 === this._numberOfCols;
    return {
      // If already merged or only 1 row and 1 column is selected, do not show mergeCells
      ...(areAllMergedCells ||
      (allColumnIndexes.length < 2 && rowIndexes.length < 2)
        ? {}
        : { mergeCells: { columnIndexes: allColumnIndexes, rowIndexes } }),
      // If all cells of selected rows are merged cells, show unmergeCells
      ...(!areAllMergedCells
        ? {}
        : {
            unmergeCells: Cell.getUnique(allSelectedCells)
              .map(cell => this._layout[cell.row][cell.column])
              .map(cell => cell.get())
          }),
      // If all columns are selected do not show deleteColumns
      ...(selectedCellsAreForAllColumns
        ? {}
        : { deleteColumns: columnIndexes }),
      // If all columns are selected and not just a single column table do not show deleteColumns
      addColumn: [last(columnIndexes)],
      duplicateColumns: allColumnIndexes,
      // If last column is selected do not show moveColumnsRight
      ...(areAllColumnsSelected || last(allColumnIndexes) === this._numberOfCols
        ? {}
        : {
            moveColumnsRight: {
              srcIndexes: allColumnIndexes,
              destIndex: last(this.selectColumns([last(allColumnIndexes) + 1])) // TODO: fix equivalent for row
            }
          }),
      // If first row is selected do not show moveRowsUp
      ...(areAllColumnsSelected || allColumnIndexes[0] === 0
        ? {}
        : {
            moveColumnsLeft: {
              srcIndexes: allColumnIndexes,
              destIndex: this.selectColumns([allColumnIndexes[0] - 1])[0]
            }
          })
    };
  }

  /**
   *
   * @param {Array} cellIds List of cell UUIDs.
   * @returns {ContextActions} Values for rendering and performing context menu actions.
   */
  getActionsForCells(cellIds) {
    validate.getActionsForCells(cellIds);
    const cells = this.selectCells(cellIds);

    // return early
    if (
      cells.some(
        cell =>
          !(cell instanceof Cell) ||
          this._isRowOutOfBounds(cell.row) ||
          this._isColumnOutOfBounds(cell.column)
      )
    ) {
      return false;
    }

    const selectedCellPositions = this._layout.flatMap((row, rowIndex) =>
      row.flatMap((cell, columnIndex) =>
        cells.flatMap(selectedCell =>
          cell.id === selectedCell.id ? [[rowIndex, columnIndex]] : []
        )
      )
    );
    const selectedRowIndexes = unique(
      selectedCellPositions.map(([rowIndex]) => rowIndex)
    );
    const selectedColumnIndexes = unique(
      selectedCellPositions.map(([_, columnIndex]) => columnIndex)
    );

    // mergeCells and unmergeCells are handled by this method
    const columnActions = !isSequential(selectedColumnIndexes)
      ? {}
      : this.getActionsForColumns(selectedColumnIndexes);
    delete columnActions.mergeCells;
    delete columnActions.unmergeCells;
    const rowActions = !isSequential(selectedRowIndexes)
      ? {}
      : this.getActionsForRows(selectedRowIndexes);
    delete rowActions.mergeCells;
    delete rowActions.unmergeCells;

    const areAllMergedCells = !cells.some(
      cell => cells.filter(selectedCell => cell.isSame(selectedCell)).length < 2
    );
    const validSelectedCellPositions = selectedRowIndexes.flatMap(rowIndex =>
      selectedColumnIndexes.map(columnIndex => [rowIndex, columnIndex])
    );
    const canSelectedCellsBeMerged =
      `${selectedCellPositions}` === `${validSelectedCellPositions}`;
    let mergeCells = {};
    if (
      cellIds.length > 1 &&
      canSelectedCellsBeMerged &&
      isSequential(selectedRowIndexes) &&
      isSequential(selectedColumnIndexes)
    ) {
      mergeCells = {
        mergeCells: {
          rowIndexes: selectedRowIndexes,
          columnIndexes: selectedColumnIndexes
        }
      };
    }
    let unmergeCells = {};
    if (areAllMergedCells) {
      unmergeCells = {
        unmergeCells: Cell.getUnique(cells)
          .map(cell => this._layout[cell.row][cell.column])
          .map(cell => cell.get())
      };
    }
    return {
      ...rowActions,
      ...columnActions,
      ...mergeCells,
      ...unmergeCells
    };
  }

  /**
   * Move column(s)
   * If scrIndexes does not include all the columns within a compound column, this method is forgiving
   * and will identify all columns.
   * When moving compound column to the right the last column in the compound column will land on the destIndex.
   * When moving to the right onto a compound column, it only land on the last column of the destination compound column.
   * When moving compound column to the left the first column in the compound column will land on the destIndex.
   * When moving to the left onto a compound column, it only land on the first column of the destination compound column.
   * NOTE: layout-specific action, no side effects for cells.
   *
   * @param {Array} srcIndexes List of sequential column indexes to move.
   * @param {number} destIndex Index of column to move columns to.
   * @returns {Object} Response with layout property.
   */
  moveColumns(srcIndexes, destIndex) {
    validate.moveColumns(srcIndexes, destIndex);
    // Return early if out of bounds
    if (
      this._isColumnOutOfBounds(destIndex) ||
      srcIndexes.some(srcIndex => this._isColumnOutOfBounds(srcIndex))
    ) {
      return false;
    }
    // Return early if destination is same as source
    const allSrcIndexes = this.selectColumns(srcIndexes);
    if (allSrcIndexes.includes(destIndex)) {
      return;
    }

    const direction =
      srcIndexes[0] < destIndex ? MOVE_SRC_RIGHT : MOVE_SRC_LEFT;
    const destIndexes = this.selectColumns([destIndex]);
    // Return early if compound column and either:
    if (destIndexes.length > 1) {
      // 1) Moving source column right and destIndex is NOT the last column of the compound column OR
      if (
        direction === MOVE_SRC_RIGHT &&
        destIndexes.slice(0, -1).includes(destIndex)
      ) {
        return;
      }
      // 2) Moving source column left and destIndex is NOT the first column of the compound row
      if (
        direction === MOVE_SRC_LEFT &&
        destIndexes.slice(1).includes(destIndex)
      ) {
        return;
      }
    }

    const startIndex =
      direction === MOVE_SRC_LEFT
        ? destIndex
        : destIndex - (allSrcIndexes.length - 1);
    this._layout.forEach(row => {
      row.splice(
        startIndex,
        0,
        ...row.splice(allSrcIndexes[0], allSrcIndexes.length)
      );
    });
    const srcOffset =
      direction === MOVE_SRC_LEFT
        ? -Math.abs(allSrcIndexes[0] - destIndex)
        : destIndex - last(allSrcIndexes);
    const suppliedColumns = [...allSrcIndexes, destIndex].sort(compareNumber);
    const columnsThatWereMoved = range(
      suppliedColumns[0],
      last(suppliedColumns)
    );
    const allColumns = range(0, this._numberOfCols);
    const ignoreColumns = allColumns.filter(
      column => !columnsThatWereMoved.includes(column)
    );
    const newSourceLocation = allSrcIndexes.map(i => i + srcOffset);
    const offset =
      direction === MOVE_SRC_RIGHT
        ? -Math.abs(allSrcIndexes.length)
        : allSrcIndexes.length;

    this._resetColumnOrder(offset, ignoreColumns, newSourceLocation, srcOffset);

    return {
      layout: this.getLayout()
    };
  }

  /**
   * Move row(s)
   * If scrIndexes does not include all the rows within a compound row, this method is forgiving
   * and will identify all rows.
   * When moving compound row to the down the last row in the compound row will land on the destIndex.
   * When moving to the down onto a compound row, it only land on the last row of the destination compound row.
   * When moving compound row to the up the first row in the compound row will land on the destIndex.
   * When moving to the up onto a compound row, it only land on the first row of the destination compound row.
   * NOTE: layout-specific action, no side effects for cells.
   *
   * @param {Array} srcIndexes List of sequential row indexes to move.
   * @param {number} destIndex Index of row to move rows.
   * @returns {Object} Response with layout property.
   */
  moveRows(srcIndexes, destIndex) {
    validate.moveRows(srcIndexes, destIndex);
    // Return early if out of bounds
    if (
      this._isRowOutOfBounds(destIndex) ||
      srcIndexes.some(srcIndex => this._isRowOutOfBounds(srcIndex))
    ) {
      return false;
    }
    // Return early if destination is same as source
    const allSrcIndexes = this.selectRows(srcIndexes);
    if (allSrcIndexes.includes(destIndex)) {
      return;
    }

    const destIndexes = this.selectRows([destIndex]);
    const direction = srcIndexes[0] < destIndex ? MOVE_SRC_DOWN : MOVE_SRC_UP;
    // Return early if compound row and either:
    if (destIndexes.length > 1) {
      // 1) Moving source row down and destIndex is NOT the last row of the compound row OR
      if (
        direction === MOVE_SRC_DOWN &&
        destIndexes.slice(0, -1).includes(destIndex)
      ) {
        return;
      }
      // 2) Moving source row up and destIndex is NOT the first row of the compound row
      if (
        direction === MOVE_SRC_UP &&
        destIndexes.slice(1).includes(destIndex)
      ) {
        return;
      }
    }

    const startIndex =
      direction === MOVE_SRC_UP
        ? destIndex
        : destIndex - (allSrcIndexes.length - 1);
    this._layout.splice(
      startIndex,
      0,
      ...this._layout.splice(allSrcIndexes[0], allSrcIndexes.length)
    );

    const srcDistance = destIndex - last(allSrcIndexes);
    const srcOffset =
      direction === MOVE_SRC_UP
        ? -Math.abs(allSrcIndexes[0] - destIndex)
        : srcDistance;
    const suppliedRows = [...allSrcIndexes, destIndex].sort(compareNumber);
    const rowsThatWereMoved = range(suppliedRows[0], last(suppliedRows));
    const allRows = range(0, this._numberOfRows);
    const ignoreRows = allRows.filter(row => !rowsThatWereMoved.includes(row));
    const newSourceLocation = allSrcIndexes.map(i => i + srcOffset);
    const offset =
      direction === MOVE_SRC_DOWN
        ? -Math.abs(allSrcIndexes.length)
        : allSrcIndexes.length;
    this._resetRowOrder(offset, ignoreRows, newSourceLocation, srcOffset);

    return {
      layout: this.getLayout()
    };
  }

  /**
   * Duplicate row(s)
   * Cannot duplicate rows if only part of a compound row is selected.
   *
   * @param {Array} rowIndexes List of sequential row indexes to duplicate.
   * @returns {Object} response with layout and cellIds properties.
   *                   cellIds is an array of objects with original
   *                   and duplicate properties.
   */
  duplicateRows(rowIndexes) {
    validate.duplicateRows(rowIndexes);
    if (rowIndexes.some(rowIndex => this._isRowOutOfBounds(rowIndex))) {
      return false;
    }
    const cellsInSuppliedRows = rowIndexes
      .map(rowIndex => this._layout[rowIndex])
      .flat();
    const isPartialCompoundRowSelected = Boolean(
      unique(
        this._layout
          .filter((row, rowIndex) => !rowIndexes.includes(rowIndex))
          .flatMap(row =>
            row.flatMap(cell =>
              cellsInSuppliedRows.filter(cellInSuppliedRows =>
                cell.isSame(cellInSuppliedRows)
              ).length
                ? [cell]
                : []
            )
          )
      ).length
    );
    if (isPartialCompoundRowSelected) {
      return false;
    }

    const actionedCells = rowIndexes.map(rowIndex =>
      this._layout[rowIndex].map(cell => ({
        original: cell,
        duplicate: new Cell({
          coordinates: [cell.row + rowIndexes.length, cell.column]
        })
      }))
    );

    const lastIndex = last(rowIndexes);
    const startIndex = lastIndex + 1;
    this._layout.splice(
      startIndex,
      0,
      ...actionedCells.map(cellsInRow =>
        cellsInRow.map(({ duplicate }) => duplicate)
      )
    );
    const srcIndexes = range(startIndex, lastIndex + rowIndexes.length);
    this._resetRowOrder(rowIndexes.length, range(0, lastIndex), srcIndexes, 0);

    return {
      layout: this.getLayout(),
      cellIds: actionedCells.map(cellsInRow =>
        cellsInRow.map(cell => ({
          original: cell.original.id,
          duplicate: cell.duplicate.id
        }))
      )
    };
  }

  /**
   * Duplicate column(s).
   * Cannot duplicate columns if only part of a compound column is selected.
   *
   * @param {Array} columnIndexes List of sequential column indexes to duplicate.
   * @returns {Object} response with layout and cellIds properties.
   *                   cellIds is an array of objects with original
   *                   and duplicate properties.
   */
  duplicateColumns(columnIndexes) {
    validate.duplicateColumns(columnIndexes);
    if (
      columnIndexes.some(columnIndex => this._isColumnOutOfBounds(columnIndex))
    ) {
      return false;
    }
    const cellsInSuppliedColumns = columnIndexes
      .map(columnIndex =>
        this._layout.flatMap(row =>
          row.flatMap((cell, i) => (columnIndex === i ? cell : []))
        )
      )
      .flat();
    const isPartialCompoundColumnSelected = this._layout
      .flatMap(row =>
        row.flatMap((cell, columnIndex) =>
          columnIndexes.includes(columnIndex) ? [] : [cell]
        )
      )
      .some(
        cell =>
          cellsInSuppliedColumns.filter(suppliedCell =>
            cell.isSame(suppliedCell)
          ).length
      );
    if (isPartialCompoundColumnSelected) {
      return false;
    }

    const actionedCells = this._layout.map((row, rowIndex) =>
      row.flatMap((cell, columnIndex) =>
        columnIndexes.includes(columnIndex)
          ? [
              {
                original: cell,
                duplicate: new Cell({ coordinates: cell.coordinates })
              }
            ]
          : []
      )
    );

    const startIndex = last(columnIndexes) + 1;
    this._layout.forEach((row, rowIndex) =>
      row.splice(
        startIndex,
        0,
        ...actionedCells[rowIndex].map(({ duplicate }) => duplicate)
      )
    );
    const srcIndexes = columnIndexes.map((_, i) => startIndex + i);
    this._resetColumnOrder(
      columnIndexes.length,
      range(0, Math.max(0, last(columnIndexes))),
      srcIndexes,
      srcIndexes.length
    );

    return {
      layout: this.getLayout(),
      cellIds: actionedCells.map(cellsInRow =>
        cellsInRow.map(cell => ({
          original: cell.original.id,
          duplicate: cell.duplicate.id
        }))
      )
    };
  }

  /**
   * Add column(s) to the right of the supplied/selected columns.
   * Adds same amount of columns as supplied selected.
   *
   * @param {Array} columnIndexes numbered indexes of columns to add below.
   * @returns {Object} response with layout and cellIds properties.
   */
  addColumns(columnIndexes) {
    validate.addColumns(columnIndexes);
    if (
      columnIndexes.some(columnIndex => this._isColumnOutOfBounds(columnIndex))
    ) {
      return false;
    }
    const startIndex = last(columnIndexes) + 1;

    const isAppendingToTable = last(columnIndexes) === this._numberOfCols;
    const newColumns = isAppendingToTable
      ? this._layout.map((_, rowIndex) =>
          columnIndexes.map(
            (v, i) => new Cell({ coordinates: [rowIndex, startIndex + i] })
          )
        )
      : this._layout.map((row, rowIndex) =>
          columnIndexes.flatMap((v, i) =>
            row.flatMap((cell, columnIndex) => {
              if (columnIndex !== startIndex) {
                return [];
              }
              return [
                new Cell({
                  coordinates: [
                    cell.row,
                    cell.column === startIndex ? cell.column + i : cell.column // maintain any colspans
                  ]
                })
              ];
            })
          )
        );

    this._layout.forEach((row, rowIndex) =>
      row.splice(startIndex, 0, ...newColumns[rowIndex])
    );
    const srcIndexes = columnIndexes.map((_, i) => startIndex + i);
    this._resetColumnOrder(
      columnIndexes.length,
      range(0, Math.max(0, last(columnIndexes))),
      srcIndexes,
      0
    );

    return {
      layout: this.getLayout(),
      cellIds: newColumns.flat().map(cell => cell.id)
    };
  }

  /**
   * Add column to the right of the supplied/selected columns.
   * Adds single column regardless of how many columns are supplied/selected.
   *
   * @param {Array} columnIndexes selected columnIndexes.
   * @returns {Object} response with layout and cellIds properties.
   */
  addColumn(columnIndexes) {
    validate.addColumns(columnIndexes);
    const lastSuppliedColumn = last(columnIndexes);
    if (
      columnIndexes.some(columnIndex => this._isColumnOutOfBounds(columnIndex))
    ) {
      return false;
    }

    const length = this._numberOfRows + 1;
    const startIndex = lastSuppliedColumn + 1;
    const isAppendingToTable = lastSuppliedColumn === this._numberOfCols;

    const newColumns = isAppendingToTable
      ? Array.from(
          { length },
          (v, i) => new Cell({ coordinates: [i, startIndex] })
        )
      : this._layout.flatMap((row, rowIndex) =>
          row.flatMap((cell, columnIndex) => {
            if (columnIndex !== startIndex) {
              return [];
            }
            return new Cell({
              coordinates: [
                cell.row,
                cell.column === startIndex ? startIndex : cell.column // maintain any colspans
              ]
            });
          })
        );

    this._layout.forEach((row, rowIndex) =>
      row.splice(startIndex, 0, newColumns[rowIndex])
    );
    this._resetColumnOrder(
      1,
      range(0, Math.max(0, lastSuppliedColumn)),
      [startIndex],
      0
    );

    return {
      layout: this.getLayout(),
      cellIds: newColumns.flat().map(cell => cell.id)
    };
  }

  /**
   * Add row(s) below the supplied/selected rows.
   * Adds same amount of rows as supplied/selected.
   *
   * @param {Array} rowIndexes numbered indexes of rows to add below.
   * @returns {Object} response with layout and cellIds properties.
   */
  addRows(rowIndexes) {
    validate.addRows(rowIndexes);
    if (rowIndexes.some(rowIndex => this._isRowOutOfBounds(rowIndex))) {
      return false;
    }
    const length = this._numberOfCols + 1;
    const startIndex = last(rowIndexes) + 1;
    const isAppendingToTable = last(rowIndexes) === this._numberOfRows;
    const newRows = isAppendingToTable
      ? rowIndexes.map((_, j) =>
          Array.from(
            { length },
            (v, i) => new Cell({ coordinates: [startIndex + j, i] })
          )
        )
      : rowIndexes.map((_, j) =>
          this._layout[startIndex].map(
            cell =>
              new Cell({
                coordinates: [
                  cell.row === startIndex ? cell.row + j : cell.row, // maintain any rowspans
                  cell.column
                ]
              })
          )
        );

    this._layout.splice(startIndex, 0, ...newRows);
    const srcIndexes = rowIndexes.map((_, i) => startIndex + i);
    this._resetRowOrder(
      rowIndexes.length,
      range(0, Math.max(0, last(rowIndexes))),
      srcIndexes,
      0
    );

    return {
      layout: this.getLayout(),
      cellIds: newRows.flat().map(cell => cell.id)
    };
  }

  /**
   * Add row below the supplied/selected rows.
   * Add single row regardless of how many rows are supplied/selected.
   *
   * @param {Array} rowIndexes selected rowIndexes.
   * @returns {Object} response with layout and cellIds properties.
   */
  addRow(rowIndexes) {
    validate.addRows(rowIndexes);
    const lastSuppliedRow = last(rowIndexes);
    if (this._isRowOutOfBounds(lastSuppliedRow)) {
      return false;
    }
    const length = this._numberOfCols + 1;
    const startIndex = lastSuppliedRow + 1;
    const isAppendingToTable = lastSuppliedRow === this._numberOfRows;
    const newRows = isAppendingToTable
      ? Array.from(
          { length },
          (v, i) => new Cell({ coordinates: [startIndex, i] })
        )
      : this._layout[startIndex].map(
          cell =>
            new Cell({
              coordinates: [
                cell.row === startIndex ? startIndex : cell.row, // maintain any rowspans
                cell.column
              ]
            })
        );
    this._layout.splice(startIndex, 0, newRows);
    this._resetRowOrder(
      1,
      range(0, Math.max(0, lastSuppliedRow)),
      [startIndex],
      0
    );

    return {
      layout: this.getLayout(),
      cellIds: newRows.flat().map(cell => cell.id)
    };
  }

  /**
   * Delete supplied/selected column(s).
   * NOTE: can delete part of a compound column.
   *
   * @param {Array} columnIndexes numbered indexes of columns to delete.
   * @returns {Object} response with layout and cellIds properties.
   */
  deleteColumns(columnIndexes) {
    validate.deleteColumns(columnIndexes);
    if (
      columnIndexes.some(columnIndex =>
        this._isColumnOutOfBounds(columnIndex)
      ) ||
      // all columns selected (except for table with single column)
      (`${columnIndexes}` !== "0" &&
        columnIndexes.toString() === range(0, this._numberOfCols).toString())
    ) {
      return false;
    }

    // Reduce colspan of merged cells (colspan > 1) if deleting only
    // a single column in a compound column. This can happen if you select a
    // non merged cell and delete column, but the column has other cell(s) that are
    // merged right into another column. If you selected one of those merged cells
    // it would delete the entire compound row.
    const nextColumnIndex = last(columnIndexes) + 1;
    if (nextColumnIndex - 1 !== this._numberOfCols) {
      columnIndexes.forEach(columnIndex => {
        // can't get orphaned reference cells when deleting last column
        const mergedRows = unique(
          this._layout.flatMap(row =>
            row
              .slice(nextColumnIndex, this._numberOfCols + 1)
              .flatMap(cell => (cell.column === columnIndex ? cell.row : []))
          )
        );

        // flip the next reference cell for the target so the next reference not the target gets deleted
        mergedRows.forEach(mergedRow => {
          [
            this._layout[mergedRow][columnIndex],
            this._layout[mergedRow][nextColumnIndex]
          ] = [
            this._layout[mergedRow][nextColumnIndex],
            this._layout[mergedRow][columnIndex]
          ];
        });

        // ensure after resetColumnOrder cell doesn't end up with -1 colIndex
        // by adjusting the coordinates for cells in columns after the deleted row
        // that are merged into the deleted row.
        mergedRows.forEach(mergedRow => {
          this._layout[mergedRow]
            .slice(nextColumnIndex, this._numberOfCols + 1)
            .forEach((column, i) => {
              const nextCell = this._layout[mergedRow][nextColumnIndex + i];
              if (nextCell.column === columnIndex) {
                nextCell.update([mergedRow, nextColumnIndex]);
              }
            });
        });
      });
    }

    const deletedCells = [];
    columnIndexes.reverse().forEach(columnIndex => {
      this._layout = this._layout.map(row => {
        deletedCells.push(row.splice(columnIndex, 1));
        return row;
      });
    });
    const ignoreIndexes =
      columnIndexes[0] === 0 ? [] : range(0, last(columnIndexes) - 1);
    this._resetColumnOrder(-Math.abs(columnIndexes.length), ignoreIndexes);

    return {
      layout: this.getLayout(),
      cellIds: deletedCells.flat().map(cell => cell.id)
    };
  }

  /**
   * Delete supplied/selected row(s).
   * NOTE: can delete part of a compound row.
   *
   * @param {Array} rowIndexes numbered indexes of rows to delete.
   * @returns {Object} response with layout and cellIds properties.
   */
  deleteRows(rowIndexes) {
    validate.deleteRows(rowIndexes);
    if (
      rowIndexes.some(rowIndex => this._isRowOutOfBounds(rowIndex)) ||
      // all rows selected (except for table with single row)
      (`${rowIndexes}` !== "0" &&
        rowIndexes.toString() === range(0, this._numberOfRows).toString())
    ) {
      return false;
    }

    // Reduce rowspan of merged cells (rowspan > 1) if deleting only
    // a single row in a compound row. This can happen if you select a
    // non merged cell and delete row, but the row has other cell(s) that are
    // merged down into another row. If you selected one of those merged cells
    // it would delete the entire compound row.
    // Check if deleting row(s) will result in any orphaned reference cells
    const nextRowIndex = last(rowIndexes) + 1;
    if (nextRowIndex - 1 !== this._numberOfRows) {
      rowIndexes.forEach(rowIndex => {
        // can't get orphaned reference cells when deleting last row
        const margedColumns = unique(
          this._layout[nextRowIndex].flatMap(cell =>
            cell.row === rowIndex ? cell.column : []
          )
        );

        // flip the next reference cell for the target so the next reference not the target gets deleted
        margedColumns.forEach(mergedColumn => {
          [
            this._layout[rowIndex][mergedColumn],
            this._layout[nextRowIndex][mergedColumn]
          ] = [
            this._layout[nextRowIndex][mergedColumn],
            this._layout[rowIndex][mergedColumn]
          ];
        });
        // ensure after resetRowOrder cell doesn't end up with -1 rowIndex
        // by adjusting the coordinates for cells in rows after the deleted row
        // that are merged into the deleted row.
        this._layout
          .slice(nextRowIndex, this._layout.length)
          .forEach((row, i) => {
            margedColumns.forEach(mergedColumn => {
              if (
                this._layout[nextRowIndex + i][mergedColumn].row === rowIndex
              ) {
                this._layout[nextRowIndex + i][mergedColumn].update([
                  nextRowIndex,
                  mergedColumn
                ]);
              }
            });
          });
      });
    }

    const deletedCells = rowIndexes
      .reverse()
      .map(i => this._layout.splice(i, 1));
    const ignoreIndexes =
      rowIndexes[0] === 0 ? [] : range(0, last(rowIndexes) - 1);
    this._resetRowOrder(-Math.abs(rowIndexes.length), ignoreIndexes);

    return {
      layout: this.getLayout(),
      cellIds: deletedCells
        .flat()
        .flat()
        .map(cell => cell.id)
    };
  }

  /**
   * Merge cells contained in the supplied rowIndexes and columnIndexes.
   * Content is flattened into target cell on merge.
   *
   * @param {Array} rowIndexes List of indexes for rows of cells to merge.
   * @param {Array} columnIndexes List of indexes for columns of cells to merge.
   * @returns {Object} response with layout and cellIds properties.
   *                   cellIds is an object with target and references properties.
   */
  mergeCells(rowIndexes, columnIndexes) {
    validate.mergeCells(rowIndexes, columnIndexes);

    // return early
    if (
      rowIndexes.some(index => this._isRowOutOfBounds(index)) ||
      columnIndexes.some(index => this._isColumnOutOfBounds(index))
    ) {
      return false;
    }
    // colspans/rowspans that extend after selection
    const columnIndexesAfterSelection = range(
      last(columnIndexes) + 1,
      this._numberOfCols
    );
    const columnsAfterSelection = this._getSelection(
      rowIndexes,
      columnIndexesAfterSelection
    );
    const anyColSpansBeyondSelection = Boolean(
      columnsAfterSelection
        .flatMap(row => row.map(cell => columnIndexes.includes(cell.column)))
        .filter(Boolean).length
    );
    const rowIndexesAfterSelection = range(
      last(rowIndexes) + 1,
      this._numberOfRows
    );
    const rowsAfterSelection = this._getSelection(
      rowIndexesAfterSelection,
      columnIndexes
    );
    const anyRowSpansBeyondSelection = Boolean(
      rowsAfterSelection
        .flatMap(row => row.map(cell => rowIndexes.includes(cell.row)))
        .filter(Boolean).length
    );
    if (anyColSpansBeyondSelection || anyRowSpansBeyondSelection) {
      return false;
    }
    // colspans/rowspans that extend before selection
    const selection = this._getSelection(rowIndexes, columnIndexes);
    const anyColSpansBeforeSelection = Boolean(
      selection.map(row => row[0].column !== columnIndexes[0]).filter(Boolean)
        .length
    );
    const anyRowSpansBeforeSelection = Boolean(
      selection[0].map(cell => cell.row !== rowIndexes[0]).filter(Boolean)
        .length
    );
    if (anyColSpansBeforeSelection || anyRowSpansBeforeSelection) {
      return false;
    }

    // merge cells
    const targetCoordinates = [
      rowIndexes.sort(compareNumber)[0],
      columnIndexes.sort(compareNumber)[0]
    ];
    const target = this._layout[targetCoordinates[0]][targetCoordinates[1]].id;
    const references = [];
    this._layout = this._layout.map((row, rowIndex) =>
      !rowIndexes.includes(rowIndex)
        ? row
        : row.map((cell, columnIndex) => {
            if (!columnIndexes.includes(columnIndex)) {
              return cell;
            }
            references.push(cell.id);
            return cell.update(targetCoordinates);
          })
    );

    return {
      layout: this.getLayout(),
      cellIds: {
        target,
        references: references.filter(cellId => cellId !== target)
      }
    };
  }

  /**
   * Unmerge cell(s)
   * NOTE: layout-specific action, no side effects for cells.
   *
   * @param {Array} cellIds List of cell UUIDs.
   * @returns {Object} response with layout.
   */
  unmergeCells(cellIds) {
    validate.unmergeCells(cellIds);
    const cells = cellIds.map(id => this._getCellById(id));

    // return early
    if (
      cells.some(
        cell =>
          !(cell instanceof Cell) ||
          this._isRowOutOfBounds(cell.row) ||
          this._isColumnOutOfBounds(cell.column)
      )
    ) {
      return false;
    }

    const listOfCellsToUnmerge = cellIds.map(cellId =>
      this._getCellById(cellId)
    );
    this._layout = this._layout.map((row, rowIndex) =>
      row.map((cell, columnIndex) => {
        // const isMergedCell = listOfCellsToUnmerge.includes(cell.toString());
        const isMergedCell = listOfCellsToUnmerge.filter(unmergeCell =>
          unmergeCell.isSame(cell)
        ).length;
        const isReferenceCell =
          rowIndex !== cell.row || columnIndex !== cell.column;
        return isMergedCell && isReferenceCell
          ? cell.update([rowIndex, columnIndex]) // replace reference with original cell position
          : cell;
      })
    );

    return {
      layout: this.getLayout()
    };
  }

  _getPositionByCellId(cellId) {
    const rowIndex = this._layout.findIndex(row =>
      row.some(cell => cell.id === cellId)
    );
    const columnIndex = this._layout[rowIndex].findIndex(
      cell => cell.id === cellId
    );
    return [rowIndex, columnIndex];
  }

  _getCleanRowIndexes(cells) {
    const rowIndexes = unique(
      cells.map(cell => this._getPositionByCellId(cell.id)[0])
    ).sort(compareNumber);
    return range(Math.min(...rowIndexes), Math.max(...rowIndexes));
  }

  _getCleanColumnIndexes(cells) {
    const columnIndexes = unique(
      cells.map(cell => this._getPositionByCellId(cell.id)[1])
    ).sort(compareNumber);
    return range(Math.min(...columnIndexes), Math.max(...columnIndexes));
  }

  _getRecursiveSelection(cells) {
    // Base selection
    const selection = this._getSelection(
      this._getCleanRowIndexes(cells),
      this._getCleanColumnIndexes(cells)
    ).flat();

    // Extend selection based on the layout
    const extendedSelection = this._layout.flatMap(row =>
      row.filter(cell =>
        selection.some(currentCell => currentCell.isSame(cell))
      )
    );

    return extendedSelection.length > selection.length
      ? this._getRecursiveSelection(extendedSelection)
      : extendedSelection;
  }

  /**
   * Get range of cells inclusive of start and end cells.
   * Returned cellIds always begin with start cell and end
   * with end cell, so the direction of the range of cells
   * may be reversed if the endCell comes before (top-left)
   * of the startCell.
   *
   * @param {Array} startCellId UUID of start cell.
   * @param {Array} endCellId UUID of end cell.
   * @returns {Array} UUIDs of cell range.
   */
  getCellRange(startCellId, endCellId) {
    const startCell = this._getCellById(startCellId);
    const endCell = this._getCellById(endCellId);

    // early return
    if (!startCell || !endCell) {
      return;
    }

    const entireSelection = this._getRecursiveSelection([startCell, endCell]);
    const rangeOfCellIds = entireSelection.map(cell => cell.id);

    // NOTE: move startCellId to always be first returned cell in the range.
    return [
      startCellId,
      ...rangeOfCellIds.filter(cellId => cellId !== startCellId)
    ];
  }
}

export default Table2EditorOps;
